import React from 'react';
import { Loader2, CheckCircle2, Download, X, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AudioFormat } from '@/types/recording';

export type DownloadStep = 'idle' | 'converting' | 'ready' | 'error';

interface DownloadProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: DownloadStep;
  progress: number; // 0-100
  format: AudioFormat;
  fileName?: string;
  errorMessage?: string;
  onDownload: () => void;
  onCancel: () => void;
}

export function DownloadProgressModal({
  isOpen,
  onClose,
  step,
  progress,
  format,
  fileName,
  errorMessage,
  onDownload,
  onCancel,
}: DownloadProgressModalProps) {
  const getStepContent = () => {
    switch (step) {
      case 'converting':
        return {
          icon: <Loader2 className="w-12 h-12 text-[#6FEDD6] animate-spin" />,
          title: '正在轉換...',
          description: `正在將錄音轉換為 ${format.toUpperCase()} 格式`,
          showProgress: true,
        };
      case 'ready':
        return {
          icon: <CheckCircle2 className="w-12 h-12 text-[#6FEDD6]" />,
          title: '轉換完成！',
          description: `${fileName || 'recording'}.${format}`,
          showProgress: false,
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-12 h-12 text-red-500" />,
          title: '轉換失敗',
          description: errorMessage || '無法完成格式轉換',
          showProgress: false,
        };
      default:
        return {
          icon: <Download className="w-12 h-12 text-gray-400" />,
          title: '準備下載',
          description: '點擊下方按鈕開始下載',
          showProgress: false,
        };
    }
  };

  const content = getStepContent();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-zinc-900 border-2 border-black rounded-2xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="sr-only">下載錄音</DialogTitle>
          <DialogDescription className="sr-only">
            {content.title} - {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-6 gap-4">
          {/* Icon */}
          <div className="p-4 rounded-full bg-zinc-800/50">
            {content.icon}
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white">
            {content.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-zinc-400 text-center">
            {content.description}
          </p>

          {/* Progress Bar */}
          {content.showProgress && (
            <div className="w-full px-4">
              <Progress value={progress} className="h-2 bg-zinc-800 [&>div]:bg-[#6FEDD6]" />
              <p className="text-center text-xs text-zinc-500 mt-2">
                {progress}%
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 w-full px-4 mt-2">
            {step === 'converting' && (
              <Button
                onClick={onCancel}
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                取消
              </Button>
            )}

            {step === 'ready' && (
              <>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  關閉
                </Button>
                <Button
                  onClick={onDownload}
                  className="flex-1 bg-[#6FEDD6] text-black hover:bg-[#5DDDC6] font-bold"
                >
                  <Download className="w-4 h-4 mr-2" />
                  儲存檔案
                </Button>
              </>
            )}

            {step === 'error' && (
              <>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  關閉
                </Button>
                <Button
                  onClick={onDownload}
                  className="flex-1 bg-[#FFD93D] text-black hover:bg-[#EEC82C] font-bold"
                >
                  下載原始 WebM
                </Button>
              </>
            )}

            {step === 'idle' && (
              <Button
                onClick={onDownload}
                className="w-full bg-[#6FEDD6] text-black hover:bg-[#5DDDC6] font-bold"
              >
                <Download className="w-4 h-4 mr-2" />
                開始下載
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
